import React from 'react'
import {compact, isEmpty} from 'lodash'
import moment, {dateIsInTheFuture, dateIsTodayOrInThePast} from 'admin/lib/moment'
import {formatNumber} from 'admin/lib/format'
import InputField from 'admin/components/shared/InputField'
import DateField from 'admin/components/shared/DateField'
import AccountField from 'admin/components/shared/AccountField'
import ShowField from 'admin/components/shared/ShowField'
import LineItems from 'admin/components/shared/LineItems'
import {calculateLineItems, calculateTotals, TAX_TYPES} from 'bundles/admin/lib/tax'

export default ({errors, organisationID, invoice, onChange, onEditEmailTemplate}) => {
  const fillBillTo = e => {
    e.preventDefault()
    const billingCustomer = invoice.account?.displayName?.toUpperCase()
    const billingAddress = invoice.account?.contactAddress
    handleChange({billTo: compact([billingCustomer, billingAddress]).join('\n')})
  }

  const editEmailTemplate = e => {
    e.preventDefault()
    onEditEmailTemplate()
  }

  const handleChange = changes => {
    let alteredChanges = {...changes}

    if ('date' in changes) {
      if (dateIsTodayOrInThePast(changes.date) || (dateIsInTheFuture(changes.date) && !invoice.email)) {
        alteredChanges.isSendable = false
      }
      let daysUntilDue = moment(invoice.dueDate).diff(moment(invoice.date), 'days')
      alteredChanges.dueDate = moment(changes.date).add(daysUntilDue, 'days').format('YYYY-MM-DD')
    } else if ('daysUntilDue' in changes) {
      delete alteredChanges.daysUntilDue
      alteredChanges.dueDate = moment(invoice.date).add(changes.daysUntilDue, 'days').format('YYYY-MM-DD')
    } else if ('accountId' in changes) {
      alteredChanges.billTo = null
    } else if ('taxType' in changes) {
      alteredChanges.lineItems = calculateLineItems(invoice.lineItems, changes.taxType)
    }

    onChange(alteredChanges)
  }

  const {subtotal, tax, total} = calculateTotals([
    ...invoice.lineItems, 
    ...invoice.credits, 
    ...invoice.debits
  ], invoice.taxType)

  return (
    <form>
      <div className='columns pb-4'>
        <div className='column is-4'>
          <AccountField
            label='Account'
            value={invoice.account?.displayName}
            organisationID={organisationID}
            onSelect={account => handleChange({account, accountId: account?.id})}
            errors={errors.accountId}
            disabled={invoice.state === 'locked'}
          />

          <div className='field is-spaced'>
            <div className='control'>
              {!invoice.billTo && invoice.account?.id && (
                <a href='' onClick={fillBillTo} className='is-pulled-right is-size-7'>
                  Use account details
                </a>
              )}
              <InputField
                type='textarea'
                name='billTo'
                label='Bill to'
                value={invoice.billTo || ''}
                onChange={handleChange}
                errors={errors.billTo}
                disabled={invoice.state === 'locked'}
                rows={4}
              />
            </div>
          </div>

          {invoice.id && dateIsInTheFuture(invoice.date) && (
            <InputField
              type='checkbox'
              name='isSendable'
              checked={invoice.isSendable || false}
              label='Send email on invoice date'
              onChange={handleChange}
              help='Only approved invoices will be sent'
              disabled={invoice.state === 'locked'}>
              {invoice.isSendable && isEmpty(errors.email) ? (
                <a
                  href=''
                  className='is-size-7'
                  onClick={editEmailTemplate}
                  style={{position: 'absolute', top: 0, right: 0}}>
                  Edit email message
                </a>
              ) : invoice.isSendable ? (
                <a
                  href=''
                  className='is-size-7 has-text-danger'
                  onClick={editEmailTemplate}
                  style={{position: 'absolute', top: 0, right: -20}}>
                  <span className='icon-text'>
                    <span className='mr-1'>Edit email message</span>
                    <span className='icon has-text-danger'>
                      <i className='fa fa-exclamation-triangle'></i>
                    </span>
                  </span>
                </a>
              ) : null}
            </InputField>
          )}
        </div>
        <div className='column is-8-tablet is-6-desktop is-offset-2-desktop'>
          <DateField
            name='date'
            label='Invoice date'
            value={invoice.date || ''}
            onChange={handleChange}
            required
            errors={errors.date}
            isHorizontal={true}
            disabled={invoice.state === 'locked'}
          />

          <ShowField label='Invoice number' value={invoice.invoiceNumber} />
          <ShowField label='Account number' value={invoice.customerNumber} />
          <ShowField label='GST number' value={invoice.gstNumber} />

          <InputField
            type='number'
            name='daysUntilDue'
            label='Payment is due'
            value={invoice.dueDate ? moment(invoice.dueDate).diff(moment(invoice.date), 'days') : ''}
            min={0}
            onChange={handleChange}
            errors={errors.dueDate}
            disabled={!invoice.date || invoice.state === 'locked'}
            isHorizontal={true}>
            <a className='button is-static'>&nbsp; day(s) after invoice date</a>
          </InputField>
        </div>
      </div>

      <LineItems
        items={invoice.lineItems || []}
        onChange={lineItems => onChange({lineItems: calculateLineItems(lineItems, invoice.taxType)})}
        errors={errors.lineItems}
        disabled={invoice.state === 'locked'}
      />

      {invoice.debits.length || invoice.credits.length ? (
        <div className='extra-line-items'>
          <div className='intro is-size-7 has-text-centered'>
            Additional transactions are attached to this invoice. They occur within the invoice period but were created
            as independent credits or debits rather than line items.
          </div>
          <div className='body'>
            {[...invoice.debits, ...invoice.credits].map(({description, unitPrice}, i) => {
              return (
                <div className='field is-grouped m-0' key={i}>
                  <div className='control is-expanded'>
                    {i === 0 && <label className='label is-small'>Description</label>}
                    <span className='value' style={{flexGrow: 2}}>
                      {description}
                    </span>
                  </div>
                  <div className='control amount has-text-right'>
                    {i === 0 && <label className='label is-small'>Amount</label>}
                    <span className='value'>
                      {formatNumber(unitPrice, {thousands: true})}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      <div className='field is-grouped is-grouped-right is-spaced'>
        <div className='control h'>
          <label className='label is-size-7'>Amounts are</label>
        </div>
        {Object.values(TAX_TYPES).map((type, i) => (
          <div key={i} className='control'>
            <label name={`taxType_${type}`} className='radio is-size-7'>
              <input
                type='radio'
                name='taxType'
                value={type}
                onChange={e => handleChange({taxType: e.target.value})}
                checked={invoice.taxType === type}
                disabled={invoice.state === 'locked'}
              />
              <b>Tax {type.toLowerCase()}</b>
            </label>
          </div>
        ))}
      </div>

      <div className='field is-grouped is-grouped-right'>
        <div className='control'>
          <label className='label is-size-7'>Subtotal</label>
        </div>
        <div className='control has-text-right' style={{width: '100px'}}>
          {formatNumber(subtotal, {thousands: true})}
        </div>
      </div>

      <div className='field is-grouped is-grouped-right'>
        <div className='control'>
          <label className='label is-size-7'>
            {invoice.taxType === TAX_TYPES.inclusive ? 'Includes GST 15%' : 'Total GST 15%'}
          </label>
        </div>
        <div className='control has-text-right' style={{width: '100px'}}>
          {formatNumber(tax, {thousands: true})}
        </div>
      </div>

      <div className='field is-grouped is-grouped-right'>
        <div className='control'>
          <label className='label is-size-4'>TOTAL</label>
        </div>
        <div className='control has-text-right is-size-4' style={{width: '100px'}}>
          {formatNumber(total, {thousands: true})}
        </div>
      </div>
    </form>
  )
}
